import React from "react";
import {
  useQvTheme,
  useQvFlavor,
  useQvLightTone,
  setStoredAppearance,
  DARK_FLAVORS,
  LIGHT_TONES,
} from "./themeManager";

/**
 * APPEARANCE picker — one choice across both worlds: the two light tones
 * (Classic = untouched default, Warm = parchment) and the four dark tones.
 *
 * Shared by the merchant Header and the superadmin AllUserHeader. The
 * component renders UNGATED — every host wraps it in the same signed-in
 * check App.js uses for the theme (any login_type).
 *
 * Inline-styled on purpose: it must render in BOTH themes, so it cannot live
 * in a gated stylesheet. Plain buttons, not MenuItems, so picking a tone
 * keeps the host menu open. The `.qv-flavor-dot` class is load-bearing:
 * dark.css excludes it from the unchecked-radio repaint rule.
 */
export default function AppearancePicker() {
  const [qvTheme] = useQvTheme();
  const [qvFlavor] = useQvFlavor();
  const [qvLightTone] = useQvLightTone();

  const activeId = qvTheme === "dark" ? qvFlavor : qvLightTone;
  const ring = qvTheme === "dark" ? "var(--qv-brand-text)" : "var(--qv-brand)";

  const dot = (t, kind) => (
    <button
      key={t.id}
      type="button"
      role="radio"
      aria-checked={activeId === t.id}
      aria-label={t.label}
      title={t.label}
      className="qv-flavor-dot"
      onClick={(e) => {
        e.stopPropagation();
        if (kind === "light") {
          if (qvTheme !== "dark" && qvLightTone === t.id) return;
          setStoredAppearance({ theme: "light", lightTone: t.id });
        } else {
          if (qvTheme === "dark" && qvFlavor === t.id) return;
          setStoredAppearance({ theme: "dark", flavor: t.id });
        }
      }}
      style={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        padding: 0,
        cursor: "pointer",
        flex: "0 0 auto",
        backgroundColor: t.swatch,
        border: "1px solid rgba(127, 134, 148, 0.6)",
        outline: activeId === t.id ? `2px solid ${ring}` : "none",
        outlineOffset: 2,
      }}
    />
  );

  return (
    <div
      className="qv-app-header-menu__appearance"
      role="radiogroup"
      aria-label="Appearance"
      style={{ padding: "8px 16px 10px" }}
    >
      <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 8, textAlign: "center" }}>
        Appearance
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          width: "100%",
        }}
      >
        {LIGHT_TONES.map((t) => dot(t, "light"))}
        <span
          aria-hidden="true"
          style={{
            width: 1,
            height: 18,
            backgroundColor: "rgba(127, 134, 148, 0.4)",
            flex: "0 0 auto",
          }}
        />
        {DARK_FLAVORS.filter((f) => f.id !== "graphite").map((f) => dot(f, "dark"))}
      </div>
    </div>
  );
}
